import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import SubPage from '#/common/components/SubPage';
import useAssetTypeQuery from '../hooks/useAssetTypeQuery';
import AssetTypeForm, { SubmitAssetType } from '../components/AssetTypeForm';
import { updateFields } from '../components/AssetTypeForm/fields';
import useAssetTypeUpdate from '../hooks/useUpdateAssetTypeMutation';
import { AssetType } from '#/graphql/types';
import useAssetTypeDelete from '../hooks/useDeleteAssetTypeMutation';
import getFormTitle from '#/common/utils/getFormTitle';

export interface AssetTypeParams {
  readonly id?: string;
}

const AssetTypePage: React.FC = () => {
  const { id } = useParams() as AssetTypeParams;
  const assetTypeQuery = useAssetTypeQuery(id);
  const assetType = assetTypeQuery.data?.getAssetType?.data;

  const name = assetType?.name;
  const title = getFormTitle(id, name, 'Asset Type');

  const { updateAssetType } = useAssetTypeUpdate();
  const submit = useCallback(
    (assetType: AssetType) => {
      if (!id) {
        return undefined;
      }

      return updateAssetType(id, assetType);
    },
    [id, updateAssetType],
  ) as SubmitAssetType;

  const { deleteAssetType: deleteAssetTypeInternal } = useAssetTypeDelete();
  const deleteAssetType = useCallback(() => {
    if (!id) {
      return undefined;
    }

    return deleteAssetTypeInternal(id);
  }, [deleteAssetTypeInternal, id]);

  return (
    <SubPage title={title} isLoading={assetTypeQuery.loading} showGoBackButton type="form">
      {assetType && (
        <AssetTypeForm
          assetType={assetType}
          title={title}
          fields={updateFields}
          submit={submit}
          deleteAssetType={deleteAssetType}
        />
      )}
    </SubPage>
  );
};

export default React.memo(AssetTypePage);
